<script setup>
import { DocumentTextIcon, DownloadIcon } from '@heroicons/vue/outline'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import BaseButtonPrimary from '../../base/BaseButtonPrimary.vue'
import { getCampaignData } from '../../../utilities/investment-process/useCampaign'
import { computed, onBeforeUnmount } from 'vue'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { i18n } from '../../../localization'
import { getPlatformName } from '../../../utilities/usePlatformSettings'
import useEventStore from '../../../utilities/useEvent'
import { showOrder } from '../../../utilities/api/order'
import { promiseHandler } from '../../../utilities/helpers'

const eventStore = useEventStore()

const manualSignature = computed(() => {
    return useInvestmentProcessDataState().createdOrder.emission_signatures.filter(
        (signature) => signature.type === 'manual'
    )
})
const sproofSignature = computed(() => {
    return useInvestmentProcessDataState().createdOrder.emission_signatures.find(
        (signature) => signature.type === 'sproof'
    )
})

// after opening the sproof signing tab, poll for the signed status so the button
// updates to "show document" as soon as the sproof webhook lands, without a manual refresh
const SIGNATURE_STATUS_POLL_INTERVAL_MS = 5000
const SIGNATURE_STATUS_POLL_MAX_ATTEMPTS = 120 // ~10 minutes at the interval above
let signatureStatusPollIntervalId = null
let signatureStatusPollAttempts = 0

const stopPollingSignatureStatus = () => {
    if (signatureStatusPollIntervalId) {
        clearInterval(signatureStatusPollIntervalId)
        signatureStatusPollIntervalId = null
    }
    window.removeEventListener('focus', refreshSignatureStatus)
}

const refreshSignatureStatus = async () => {
    const dataState = useInvestmentProcessDataState()
    if (sproofSignature.value?.signed_at || signatureStatusPollAttempts >= SIGNATURE_STATUS_POLL_MAX_ATTEMPTS) {
        stopPollingSignatureStatus()
        return
    }
    signatureStatusPollAttempts++
    const [data] = await promiseHandler(showOrder(dataState.createdOrder.order_uid))
    if (data) {
        dataState.setCreatedOrder(data.data.order)
        if (sproofSignature.value?.signed_at) {
            stopPollingSignatureStatus()
        }
    }
}

const startPollingSignatureStatus = () => {
    if (signatureStatusPollIntervalId) {
        return
    }
    signatureStatusPollAttempts = 0
    window.addEventListener('focus', refreshSignatureStatus)
    signatureStatusPollIntervalId = setInterval(refreshSignatureStatus, SIGNATURE_STATUS_POLL_INTERVAL_MS)
}

onBeforeUnmount(() => {
    stopPollingSignatureStatus()
})

const openSproofSignature = async () => {
    await eventStore.trackEvent({
        event: 'signature_process',
        data: {
            provider: 'sproof',
            status: 'started',
        },
        platform_name: getPlatformName(),
    })
    window.open(sproofSignature.value.link, '_blank')
    if (!sproofSignature.value.signed_at) {
        startPollingSignatureStatus()
    }
}

const openManualSignature = (signature) => {
    eventStore.trackEvent({
        event: 'signature_process',
        data: {
            provider: 'manual',
            status: 'opened',
        },
        platform_name: getPlatformName(),
    })
    window.open(signature.unsigned_document_link, '_blank')
    // manual signatures are signed offline, so downloading the document is the
    // only signal we get to unblock payment
    useInvestmentProcessDataState().acknowledgeManualSignature(signature.id)
}

const signatureNotice = computed(() => {
    if (i18n.global.t('investment_process.signature_legal_notice')) {
        return i18n.global.t('investment_process.signature_legal_notice')
    } else {
        return getCampaignData().asset_data.signature_legal_notice
    }
})
</script>

<template>
    <div v-if="sproofSignature || manualSignature" class="overflow-hidden rounded-lg border">
        <div class="px-6 py-4">
            <h3 class="flex text-xl font-bold text-gray-900">
                <DocumentTextIcon v-if="manualSignature.length === 1" class="mr-2 w-5" />
                {{ $t('investment_process.signature_legal_notice_title') }}
            </h3>
            <p class="my-2 font-medium text-gray-500" v-html="signatureNotice"></p>
            <div class="flex flex-col items-stretch gap-3">
                <component
                    :is="sproofSignature.signed_at ? BaseButtonMuted : BaseButtonPrimary"
                    v-if="sproofSignature"
                    size="lg"
                    class="w-full"
                    @click="openSproofSignature">
                    <span v-if="sproofSignature.signed_at">
                        {{ $t('investment_process.show_document') }}
                    </span>
                    <span v-else>
                        {{ $t('investment_process.sign_digitally') }}
                    </span>
                </component>
                <div v-if="manualSignature.length > 0 && sproofSignature" class="text-center text-gray-400">
                    {{ $t('investment_process.or') }}
                </div>
                <BaseButtonMuted
                    v-if="manualSignature.length === 1"
                    size="lg"
                    class="w-full"
                    @click="openManualSignature(manualSignature[0])">
                    {{ $t('investment_process.download_signature_document') }}
                </BaseButtonMuted>
                <div
                    v-if="manualSignature.length > 1"
                    class="flex w-full flex-col items-start justify-start space-y-2 rounded-md bg-slate-100 p-2">
                    <div
                        v-for="(signature, index) in manualSignature"
                        :key="signature.unsigned_document_link"
                        class="flex h-10 w-full items-center justify-between rounded-md bg-white px-2 duration-150 hover:bg-slate-50">
                        <div class="flex items-center">
                            <DocumentTextIcon class="mr-2 w-5" />
                            <p class="text-sm text-gray-500">
                                {{ $t(`investment_process.signature_document`) }} {{ index + 1 }}
                            </p>
                        </div>
                        <DownloadIcon class="w-5 cursor-pointer" @click="openManualSignature(signature)" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
